import React, { useCallback, useEffect, useState } from 'react';
import { finesService, errorMessage } from '../../../services/finesService';
import { money, Notice } from './collectionsShared';
import Button from '../../../ui/Button';
import Badge from '../../../ui/Badge';
import Card from '../../../ui/Card';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import LoadingSpinner from '../../../components/LoadingSpinner';

// The day-13 branch e-mails (owner, 20 Oct 2026): every day at 06:00 the engine rebuilds the list from Ejar and sends
// each airport branch its own e-mail - CC-block contracts only, # | Contract | Customer | Branch | Deposit |
// Block expires | Ejar balance | To collect. Here: who gets what, what was sent today, a preview, send now / a test.
const contracts = (n) => `${n} contract${n === 1 ? '' : 's'}`;
const sentLine = (s) => (s
    ? `Sent ${s.at}${s.by && s.by !== 'schedule' ? ` by ${s.by}` : ' (06:00 schedule)'} · ${contracts(s.n)} · AED ${money(s.total)}`
    : 'Not sent today');

export default function Day13BranchEmails() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState('');
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const [open, setOpen] = useState('');        // the branch whose preview is shown

    const load = useCallback(async () => {
        setBusy('Loading the branch e-mails…'); setErr('');
        try { setData(await finesService.collectionsEmailPreview()); } catch (e) { setErr(errorMessage(e)); }
        finally { setBusy(''); }
    }, []);
    useEffect(() => { load(); }, [load]);

    // kind '' = the reminders to the branches; 'followup' = the day-14 follow-up to the managers (the branch in CC)
    const send = async (test, kind = '') => {
        const fu = kind === 'followup';
        const going = ((fu ? data?.followups : data?.branches) || []).filter((b) => b.willSend);
        if (!going.length) {
            setMsg(fu ? 'Nothing to send - every contract on its last day today is charged (or none).'
                : 'Nothing to send - no branch has a CC-block contract to collect.');
            return;
        }
        const what = fu ? 'follow-up' : 'branch';
        const again = going.filter((b) => (fu ? b.lastSent?.emailed : b.lastSent)).map((b) => `${b.branch} at ${b.lastSent.at}`);
        const text = test
            ? `Send the ${going.length} ${what} e-mail(s) to your own address only (a test)?`
            : `${again.length ? `Already sent today: ${again.join(', ')}. ` : ''}Send now to ${going.map((b) => `${b.branch} (${b.to.join(', ')})`).join(' and ')} with the CC?${fu ? ' Each contract is read from Ejar first (about 10 s each).' : ''}`;
        // eslint-disable-next-line no-alert
        if (!window.confirm(text)) return;
        setBusy(test ? 'Sending the test…' : `Sending the ${what} e-mails…`); setErr(''); setMsg('');
        try {
            const res = await finesService.collectionsEmailSend(test, kind);
            const ok = (res.sent || []).map((s) => `${s.branch} → ${s.to.join(', ')}`).join(' · ');
            const bad = (res.failed || []).map((f) => `${f.branch}: ${f.error}`).join(' · ');
            if (bad) setErr(`Not sent: ${bad}`);
            setMsg(ok ? `${test ? 'Test sent' : 'Sent'}: ${ok}` : 'Nothing was sent.');
            if (!test) await load();
        } catch (e) { setErr(errorMessage(e)); }
        finally { setBusy(''); }
    };

    return (
        <Card padding="md" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h4 className="text-sm font-semibold text-gray-900">Branch e-mails · every day at {data?.time || '06:00'}</h4>
                    <p className="text-xs text-gray-600">
                        CC-block contracts only, one e-mail per branch, from {data?.from || 'the Gmail box of the daily report'}.
                        The list is rebuilt from Ejar first (tried again every 15 min while Ejar does not answer).
                    </p>
                </div>
                <div className="ds-toolbar">
                    <Button size="sm" onClick={load} disabled={!!busy} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Reload</Button>
                    <Button size="sm" onClick={() => send(true)} disabled={!!busy || !data}>Send test to me</Button>
                    <Button variant="primary" size="sm" onClick={() => send(false)} disabled={!!busy || !data} leadingIcon={<Icon glyph={ICONS.mail} size="sm" />}>Send now to branches</Button>
                </div>
            </div>
            {busy && <div className="flex items-center gap-2 text-sm text-gray-700" role="status"><LoadingSpinner size="sm" />{busy}</div>}
            {msg && <Notice tone="success">{msg}</Notice>}
            {err && <Notice tone="danger">{err}</Notice>}
            {data?.unread?.length > 0 && (
                <Notice tone="warning">
                    {data.unread.length} contract(s) could not be read from Ejar and are not in the e-mails: {data.unread.slice(0, 5).join(' · ')}
                </Notice>
            )}
            {data && (
                <div className="flex flex-col gap-3">
                    {data.branches.map((b) => (
                        <div key={b.branch} className="rounded-lg border border-gray-200 p-3 flex flex-col gap-2">
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                                <Badge variant={b.branch === 'T2' ? 'info' : 'accent'} size="sm">{b.branch}</Badge>
                                <span className="text-gray-900 font-medium">To: {b.to.join(', ')}</span>
                                <span className="text-gray-600">CC: {b.cc.join(', ')}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                                {b.willSend
                                    ? <span className="font-semibold text-red-700">{contracts(b.n)} · AED {money(b.total)}</span>
                                    : <span className="text-gray-500">Nothing to collect - no e-mail</span>}
                                <Badge variant={b.lastSent ? 'success' : 'neutral'} size="sm">{sentLine(b.lastSent)}</Badge>
                                {b.willSend && (
                                    <Button size="sm" variant="ghost" onClick={() => setOpen(open === b.branch ? '' : b.branch)}>
                                        {open === b.branch ? 'Hide preview' : 'Preview'}
                                    </Button>
                                )}
                            </div>
                            {open === b.branch && (
                                <div className="flex flex-col gap-1">
                                    <div className="text-xs text-gray-600">Subject: <b>{b.subject}</b></div>
                                    <iframe title={`${b.branch} e-mail preview`} sandbox="" srcDoc={b.html}
                                        className="w-full rounded border border-gray-200 bg-white" style={{ height: 420 }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {data?.followups && (
                <div className="flex flex-col gap-2 border-t border-gray-200 pt-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-900">Day-14 follow-up · every day at {data.followupTime || '10:00'}</h4>
                            <p className="text-xs text-gray-600">
                                The contracts the branch was asked to charge on day 12 / 13 that are on their last day today, read from
                                Ejar at {data.followupTime || '10:00'} (the preview below uses the morning list). To the managers, the
                                branch in CC - only when something is not fully charged.
                            </p>
                        </div>
                        <div className="ds-toolbar">
                            <Button size="sm" onClick={() => send(true, 'followup')} disabled={!!busy}>Send test to me</Button>
                            <Button variant="primary" size="sm" onClick={() => send(false, 'followup')} disabled={!!busy} leadingIcon={<Icon glyph={ICONS.mail} size="sm" />}>Send follow-up now</Button>
                        </div>
                    </div>
                    {data.followups.map((b) => (
                        <div key={`fu-${b.branch}`} className="rounded-lg border border-gray-200 p-3 flex flex-col gap-2">
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                                <Badge variant={b.branch === 'T2' ? 'info' : 'accent'} size="sm">{b.branch}</Badge>
                                <span className="text-gray-900 font-medium">To: {b.to.join(', ')}</span>
                                <span className="text-gray-600">CC: {b.cc.join(', ')}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                                {b.n
                                    ? <span className={`font-semibold ${b.willSend ? 'text-red-700' : 'text-green-700'}`}>
                                        {contracts(b.n)} on their last day · {b.notCharged} not fully charged · AED {money(b.stillOwed)} still to charge
                                    </span>
                                    : <span className="text-gray-500">No contract on its last day today - no e-mail</span>}
                                {b.n > 0 && !b.willSend && <span className="text-gray-500">All charged - no e-mail</span>}
                                <Badge variant={b.lastSent?.emailed ? 'success' : 'neutral'} size="sm">
                                    {b.lastSent?.emailed ? `Sent ${b.lastSent.at}${b.lastSent.by && b.lastSent.by !== 'schedule' ? ` by ${b.lastSent.by}` : ' (06:00 schedule)'}` : 'Not sent today'}
                                </Badge>
                                {b.willSend && (
                                    <Button size="sm" variant="ghost" onClick={() => setOpen(open === `fu-${b.branch}` ? '' : `fu-${b.branch}`)}>
                                        {open === `fu-${b.branch}` ? 'Hide preview' : 'Preview'}
                                    </Button>
                                )}
                            </div>
                            {b.unread?.length > 0 && <Notice tone="warning">Ejar did not give {b.unread.join(', ')} - not in the follow-up.</Notice>}
                            {open === `fu-${b.branch}` && (
                                <div className="flex flex-col gap-1">
                                    <div className="text-xs text-gray-600">Subject: <b>{b.subject}</b></div>
                                    <iframe title={`${b.branch} follow-up preview`} sandbox="" srcDoc={b.html}
                                        className="w-full rounded border border-gray-200 bg-white" style={{ height: 420 }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}
